// =====================================================
// CLINICAL RECORD FORM - Formulario Dinámico de Historia Clínica
// Genera formularios basados en plantillas configurables
// =====================================================

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  Save,
  Loader2,
  AlertCircle,
  CheckCircle,
  User,
  Calendar,
  FileText,
  ChevronDown,
  ChevronUp,
  Search,
  X,
  Plus,
  Paperclip,
  Clock,
  Info,
  // Íconos para plantillas
  Stethoscope,
  ClipboardList,
  Baby,
  Heart,
  Shield,
  Users,
  Activity,
  Pill,
  Syringe,
  Thermometer,
  Eye,
  Brain,
  Bone,
  HeartPulse,
  Edit3,
  Trash2,
  Settings2,
  type LucideIcon
} from 'lucide-react';

import {
  ClinicalTemplate,
  ClinicalRecord,
  TemplateSection,
  TemplateField,
  PatientSearchResult,
  PatientFormData,
  CreateRecordRequest,
  SelectOption
} from '../../types/clinicalRecords.types';

import clinicalRecordsApi from '../../services/clinicalRecords.api';
import DrawingCanvas from './DrawingCanvas';
import TextareaWithAttachments from './TextareaWithAttachments';

// =====================================================
// HELPER PARA ÍCONOS DINÁMICOS
// =====================================================

const iconMap: Record<string, LucideIcon> = {
  'stethoscope': Stethoscope,
  'clipboard': ClipboardList,
  'clipboard-list': ClipboardList,
  'baby': Baby,
  'heart': Heart,
  'shield': Shield,
  'users': Users,
  'child': Users,  // Alias para child
  'activity': Activity,
  'pill': Pill,
  'syringe': Syringe,
  'thermometer': Thermometer,
  'eye': Eye,
  'brain': Brain,
  'bone': Bone,
  'heart-pulse': HeartPulse,
  'file-text': FileText,
};

interface DynamicIconProps {
  name: string;
  className?: string;
  size?: number;
}

const DynamicIcon: React.FC<DynamicIconProps> = ({ name, className = "w-6 h-6", size }) => {
  const IconComponent = iconMap[name?.toLowerCase()];

  if (IconComponent) {
    return <IconComponent className={className} size={size} />;
  }

  // Fallback: ícono por defecto (nunca mostrar el nombre como texto)
  return <ClipboardList className={className} size={size} />;
};

// =====================================================
// COMPONENTE DE BÚSQUEDA DE PACIENTES
// =====================================================


// COMPONENTE DE BÚSQUEDA DE PACIENTES - ACTUALIZADO
// Con botón "+ Nuevo Paciente" siempre visible


interface PatientSearchProps {
  onSelect: (patient: PatientSearchResult | null) => void;
  selectedPatient: PatientFormData | null;
}

const PatientSearch: React.FC<PatientSearchProps> = ({ onSelect, selectedPatient }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<PatientSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isNewPatient, setIsNewPatient] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  
  // Cerrar dropdown al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Buscar pacientes
  useEffect(() => {
    const searchPatients = async () => {
      if (query.length < 2) {
        setResults([]);
        return;
      }
      
      setLoading(true);
      try {
        const response = await clinicalRecordsApi.searchPatients(query);
        setResults(response.patients || []);
        setShowDropdown(true);
      } catch (err) {
        console.error('Error buscando pacientes:', err);
      } finally {
        setLoading(false);
      }
    };
    
    const debounce = setTimeout(searchPatients, 300);
    return () => clearTimeout(debounce);
  }, [query]);
  
  const handleSelect = (patient: PatientSearchResult) => {
    onSelect(patient);
    setQuery(patient.patient_name);
    setShowDropdown(false);
    setIsNewPatient(false);
  };
  
  const handleNewPatient = () => {
    setIsNewPatient(true);
    setQuery('');
    onSelect(null);
    setShowDropdown(false);
  };
  
  const handleClear = () => {
    setQuery('');
    setResults([]);
    setIsNewPatient(false);
    onSelect(null);
  };
  
  // Si hay un paciente seleccionado, mostrar su info
  if (selectedPatient && !isNewPatient) {
    return (
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
              <User className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="font-semibold text-gray-900">{selectedPatient.patient_name}</p>
              <p className="text-sm text-gray-500">
                {selectedPatient.patient_dni && `DNI: ${selectedPatient.patient_dni}`}
                {selectedPatient.patient_email && ` • ${selectedPatient.patient_email}`}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleClear}
            className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div ref={searchRef} className="space-y-4">
      {/* Fila con buscador y botón Nuevo Paciente */}
      <div className="flex flex-col sm:flex-row gap-3">
        {/* Buscador */}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => query.length >= 2 && setShowDropdown(true)}
            placeholder="Buscar paciente por nombre, DNI o email..."
            className={`w-full pl-10 pr-10 py-3 bg-gray-50 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
              isNewPatient ? 'border-gray-200 opacity-50' : 'border-gray-200'
            }`}
            disabled={isNewPatient}
          />
          {loading && (
            <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 animate-spin" />
          )}
        </div>
        
        {/* Botón Nuevo Paciente - SIEMPRE VISIBLE */}
        <button
          type="button"
          onClick={handleNewPatient}
          className={`flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-medium transition-all whitespace-nowrap ${
            isNewPatient
              ? 'bg-blue-500 text-white shadow-md'
              : 'bg-blue-50 text-blue-600 hover:bg-blue-100 border border-blue-200'
          }`}
        >
          <Plus className="w-5 h-5" />
          <span className="hidden sm:inline">Nuevo Paciente</span>
          <span className="sm:hidden">Nuevo</span>
        </button>
      </div>
      
      {/* Dropdown de resultados de búsqueda */}
      {showDropdown && !isNewPatient && (query.length >= 2 || results.length > 0) && (
        <div className="absolute z-50 w-full max-w-[calc(100%-2rem)] sm:max-w-lg mt-1 bg-white rounded-xl shadow-lg border border-gray-200 max-h-64 overflow-y-auto">
          {results.length > 0 ? (
            <>
              {results.map((patient, index) => (
                <button
                  key={patient.patient_identifier || index}
                  type="button"
                  onClick={() => handleSelect(patient)}
                  className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors flex items-center gap-3 border-b border-gray-100 last:border-0"
                >
                  <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                    <User className="w-4 h-4 text-gray-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">{patient.patient_name}</p>
                    <p className="text-xs text-gray-500 truncate">
                      {patient.patient_dni && `DNI: ${patient.patient_dni}`}
                      {patient.patient_email && ` • ${patient.patient_email}`}
                    </p>
                  </div>
                </button>
              ))}
            </>
          ) : query.length >= 2 ? (
            <div className="p-4 text-center">
              <p className="text-gray-500 text-sm">No se encontraron pacientes con "{query}"</p>
              <p className="text-xs text-gray-400 mt-1">Usá el botón "Nuevo Paciente" para agregarlo</p>
            </div>
          ) : null}
        </div>
      )}
      
      {/* Indicador de modo nuevo paciente */}
      {isNewPatient && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                <Plus className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="font-semibold text-gray-900">Nuevo Paciente</p>
                <p className="text-sm text-gray-500">Completá los datos del paciente abajo</p>
              </div>
            </div>
            <button
              type="button"
              onClick={handleClear}
              className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
              title="Cancelar y buscar paciente existente"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// =====================================================
// COMPONENTE DE CAMPO DINÁMICO
// =====================================================

interface DynamicFieldProps {
  field: TemplateField;
  value: any;
  onChange: (name: string, value: any) => void;
  error?: string;
  autofilled?: boolean;
}

const DynamicField: React.FC<DynamicFieldProps> = ({ field, value, onChange, error, autofilled = false }) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    let newValue: any = e.target.value;
    
    if (field.field_type === 'number') {
      newValue = e.target.value === '' ? '' : Number(e.target.value);
    } else if (field.field_type === 'checkbox') {
      newValue = (e.target as HTMLInputElement).checked;
    }
    
    onChange(field.field_key, newValue);
  };
  
  const handleCheckboxChange = (optionValue: string, checked: boolean) => {
    const currentValues = Array.isArray(value) ? value : [];
    const newValues = checked
      ? [...currentValues, optionValue]
      : currentValues.filter((v: string) => v !== optionValue);
    onChange(field.field_key, newValues);
  };
  
  const baseInputClass = `w-full px-4 py-3 bg-gray-50 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
    error ? 'border-red-300 bg-red-50' : 'border-gray-200'
  }`;
  
  const widthClass = field.width === 'half' ? 'sm:col-span-1' : field.width === 'third' ? 'sm:col-span-1' : 'sm:col-span-2';
  
  return (
    <div className={widthClass}>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {field.field_label}
        {field.is_required && <span className="text-red-500 ml-1">*</span>}
        {autofilled && (
          <span
            className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium bg-purple-50 text-purple-700 border border-purple-200 rounded-full"
            title="Auto-completado con los datos que el paciente ingresó al agendar el turno. Podés modificarlo."
          >
            ✨ Datos del turno
          </span>
        )}
      </label>
      
      {field.help_text && (
        <p className="text-xs text-gray-500 mb-2 flex items-center gap-1">
          <Info className="w-3 h-3" />
          {field.help_text}
        </p>
      )}
      
      {/* Text input */}
      {field.field_type === 'text' && (
        <input
          type="text"
          value={value || ''}
          onChange={handleChange}
          placeholder={field.placeholder}
          className={baseInputClass}
        />
      )}
      
      {/* Textarea */}
      {field.field_type === 'textarea' && (
        <textarea
          value={value || ''}
          onChange={handleChange}
          placeholder={field.placeholder}
          rows={4}
          className={baseInputClass}
        />
      )}
      
      {/* Number input */}
      {field.field_type === 'number' && (
        <input
          type="number"
          value={value ?? ''}
          onChange={handleChange}
          placeholder={field.placeholder}
          min={field.validation_rules?.min}
          max={field.validation_rules?.max}
          className={baseInputClass}
        />
      )}
      
      {/* Date input */}
      {field.field_type === 'date' && (
        <input
          type="date"
          value={value || ''}
          onChange={handleChange}
          className={baseInputClass}
        />
      )}
      
      {/* Select dropdown */}
      {field.field_type === 'select' && (
        <select
          value={value || ''}
          onChange={handleChange}
          className={baseInputClass}
        >
          <option value="">{field.placeholder || 'Seleccionar...'}</option>
          {field.options?.map((opt: string | SelectOption, idx: number) => {
            const optValue = typeof opt === 'string' ? opt : opt.value;
            const optLabel = typeof opt === 'string' ? opt : opt.label;
            return (
              <option key={idx} value={optValue}>
                {optLabel}
              </option>
            );
          })}
        </select>
      )}
      
      {/* Radio buttons */}
      {field.field_type === 'radio' && (
        <div className="space-y-2">
          {field.options?.map((opt: string | SelectOption, idx: number) => {
            const optValue = typeof opt === 'string' ? opt : opt.value;
            const optLabel = typeof opt === 'string' ? opt : opt.label;
            return (
              <label key={idx} className="flex items-center gap-3 cursor-pointer">
                <input
                  type="radio"
                  name={field.field_key}
                  value={optValue}
                  checked={value === optValue}
                  onChange={handleChange}
                  className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                />
                <span className="text-gray-700">{optLabel}</span>
              </label>
            );
          })}
        </div>
      )}
      
      {/* Checkboxes */}
      {field.field_type === 'checkbox' && (
        <div className="space-y-2">
          {field.options?.map((opt: string | SelectOption, idx: number) => {
            const optValue = typeof opt === 'string' ? opt : opt.value;
            const optLabel = typeof opt === 'string' ? opt : opt.label;
            const isChecked = Array.isArray(value) && value.includes(optValue);
            return (
              <label key={idx} className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={(e) => handleCheckboxChange(optValue, e.target.checked)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-gray-700">{optLabel}</span>
              </label>
            );
          })}
        </div>
      )}
      
      {/* Drawing canvas (para árbol genealógico, etc.) */}
      {field.field_type === 'drawing' && (
        <DrawingCanvas
          value={value || ''}
          onChange={(base64) => onChange(field.field_key, base64)}
          label={field.field_label}
        />
      )}
      
      {/* Textarea con adjuntos (para exámenes complementarios, etc.) */}
      {field.field_type === 'textarea_with_attachments' && (
        <TextareaWithAttachments
          textValue={typeof value === 'string' ? value : (value?.text || '')}
          filesValue={typeof value === 'string' ? [] : (value?.files || [])}
          onTextChange={(text) => {
            const currentFiles = typeof value === 'string' ? [] : (value?.files || []);
            onChange(field.field_key, { text, files: currentFiles });
          }}
          onFilesChange={(files) => {
            const currentText = typeof value === 'string' ? value : (value?.text || '');
            onChange(field.field_key, { text: currentText, files });
          }}
          placeholder={field.placeholder}
          error={error}
        />
      )}
      
      {/* Error message */}
      {error && (
        <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
          <AlertCircle className="w-3.5 h-3.5" />
          {error}
        </p>
      )}
    </div>
  );
};

// =====================================================
// COMPONENTE DE SECCIÓN COLAPSABLE
// =====================================================

interface CollapsibleSectionProps {
  section: TemplateSection;
  formData: Record<string, any>;
  errors: Record<string, string>;
  onChange: (name: string, value: any) => void;
  defaultExpanded?: boolean;
  autofilledKeys?: Set<string>;
}

const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
  section,
  formData,
  errors,
  onChange,
  defaultExpanded = true,
  autofilledKeys
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  
  const sectionErrors = section.fields.filter((f: TemplateField) => errors[f.field_key]).length;
  
  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-5 py-4 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors"
      >
        <div className="flex items-center gap-3">
          <h3 className="font-semibold text-gray-900">{section.name}</h3>
          {sectionErrors > 0 && (
            <span className="px-2 py-0.5 bg-red-100 text-red-600 text-xs rounded-full">
              {sectionErrors} error{sectionErrors !== 1 ? 'es' : ''}
            </span>
          )}
        </div>
        {isExpanded ? (
          <ChevronUp className="w-5 h-5 text-gray-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-400" />
        )}
      </button>
      
      {isExpanded && (
        <div className="p-5">
          {section.description && (
            <p className="text-sm text-gray-500 mb-4">{section.description}</p>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {section.fields
              .sort((a: TemplateField, b: TemplateField) => a.display_order - b.display_order)
              .map((field: TemplateField) => (
                <DynamicField
                  key={field.id}
                  field={field}
                  value={formData[field.field_key]}
                  onChange={onChange}
                  error={errors[field.field_key]}
                  autofilled={autofilledKeys?.has(field.field_key)}
                />
              ))}
          </div>
        </div>
      )}
    </div>
  );
};

// =====================================================
// COMPONENTE PRINCIPAL DEL FORMULARIO
// =====================================================

const ClinicalRecordForm: React.FC = () => {
  const navigate = useNavigate();
  const { recordId } = useParams();
  const [searchParams] = useSearchParams();
  const isEditing = !!recordId;
  
  // Estados
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  
  const [templates, setTemplates] = useState<ClinicalTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<ClinicalTemplate | null>(null);
  const [patient, setPatient] = useState<PatientFormData | null>(null);
  const [isNewPatient, setIsNewPatient] = useState(false);
  
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  // Keys de campos que se llenaron automaticamente desde los datos extras del turno.
  // Se usan para mostrar el chip "✨ Datos del turno" junto al label de cada uno.
  const [autofilledKeys, setAutofilledKeys] = useState<Set<string>>(new Set());
  // ID de la plantilla que se esta cargando (para mostrar spinner inline en la card).
  // Distinto del loading global porque cuando se elige plantilla queremos feedback
  // visual especifico sin tapar toda la pantalla.
  const [loadingTemplateId, setLoadingTemplateId] = useState<number | null>(null);
  
  const [recordDate, setRecordDate] = useState(
    new Date().toISOString().split('T')[0]
  );
  const [notes, setNotes] = useState('');
  
  // Campos para nuevo paciente
  const [newPatientData, setNewPatientData] = useState({
    patient_name: '',
    patient_email: '',
    patient_phone: '',
    patient_dni: '',
    patient_birth_date: ''
  });

  // Cargar datos iniciales
  useEffect(() => {
    const loadInitialData = async () => {
      setLoading(true);
      try {
        // Cargar plantillas
        const templatesRes = await clinicalRecordsApi.getTemplates();
        setTemplates(templatesRes.templates || []);
        
        // Si estamos editando, cargar el registro
        if (isEditing && recordId) {
          const recordRes = await clinicalRecordsApi.getRecordById(Number(recordId));
          const record = recordRes.record;
          
          // Cargar plantilla del registro
          const templateRes = await clinicalRecordsApi.getTemplateById(record.template_id);
          setSelectedTemplate(templateRes.template);
          
          // Cargar datos del paciente
          setPatient({
            patient_id: record.patient_id,
            patient_name: record.patient_name,
            patient_email: record.patient_email,
            patient_phone: record.patient_phone,
            patient_dni: record.patient_dni,
            patient_birth_date: record.patient_birth_date,
            is_new_patient: false
          });
          
          // Cargar datos del formulario
          setFormData(record.form_data || {});
          setRecordDate(record.record_date.split('T')[0]);
          setNotes(record.notes || '');
        }
      } catch (err: any) {
        console.error('Error cargando datos:', err);
        setError(err.message || 'Error al cargar datos');
      } finally {
        setLoading(false);
      }
    };
    
    loadInitialData();
  }, [isEditing, recordId]);
  
  // Precargar paciente desde query params (cuando se hace click en + desde la lista)
  useEffect(() => {
    if (isEditing || patient) return; // No precargar si estamos editando o ya hay paciente
    
    const patientParam = searchParams.get('patient');
    const dniParam = searchParams.get('dni');
    const emailParam = searchParams.get('email');
    const phoneParam = searchParams.get('phone');
    
    if (patientParam || dniParam) {
      const searchQuery = dniParam || patientParam || '';
      if (searchQuery.length >= 2) {
        clinicalRecordsApi.searchPatients(searchQuery).then(res => {
          if (res.patients && res.patients.length > 0) {
            const found = res.patients[0];
            setPatient({
              patient_id: found.patient_id,
              patient_name: found.patient_name,
              patient_email: found.patient_email,
              patient_phone: found.patient_phone,
              patient_dni: found.patient_dni,
              patient_birth_date: found.patient_birth_date,
              is_new_patient: false
            });
          } else {
            // Fallback: usar datos de los query params directamente (paciente compartido)
            setPatient({
              patient_name: patientParam || '',
              patient_email: emailParam || undefined,
              patient_phone: phoneParam || undefined,
              patient_dni: dniParam || undefined,
              is_new_patient: true
            });
          }
        }).catch(err => {
          console.error('Error precargando paciente:', err);
          // Fallback en caso de error
          setPatient({
            patient_name: patientParam || '',
            patient_email: emailParam || undefined,
            patient_phone: phoneParam || undefined,
            patient_dni: dniParam || undefined,
            is_new_patient: true
          });
        });
      }
    }
  }, [searchParams, isEditing]);
  
  // Cargar plantilla completa al seleccionar
  const handleTemplateSelect = async (templateId: number) => {
    // Evitar doble click mientras carga
    if (loadingTemplateId !== null) return;
    try {
      setLoading(true);
      setLoadingTemplateId(templateId);
      const response = await clinicalRecordsApi.getTemplateById(templateId);
      setSelectedTemplate(response.template);

      // Pre-llenar campos desde datos del turno (si viene de un appointment).
      // Estrategia: matching por field_label normalizado contra labels de
      // booking_extra_fields, no por field_key (que tienen prefijos auto-generados
      // del estilo "datos_filiatorios_dni__1" que nunca matchearian un key de booking
      // como "dni"). El matching cubre tres niveles:
      //   1. Match exacto normalizado (ej: "dni" == "dni")
      //   2. Substring (ej: "obra social" en "obra social y n de afiliado")
      //   3. Match por tokens (ej: tokens de "motivo medico derivante"
      //      vs tokens de "derivacion medico derivante" comparten "medico" y "derivante")
      // Si varios keys del booking matchean el mismo field del template,
      // los valores se concatenan con " / ".
      const fromAppointment = searchParams.get('fromAppointment');
      let prefillData: Record<string, any> = {};
      let prefilledKeySet = new Set<string>();
      if (fromAppointment === 'true') {
        try {
          const extraDataStr = sessionStorage.getItem('appointmentExtraData');
          if (extraDataStr) {
            const extraData = JSON.parse(extraDataStr);
            const template = response.template;

            // Normalizador: lowercase, sin tildes, sin signos, sin "º/°/ª", colapsa espacios
            const normalize = (s: string): string =>
              (s || '')
                .toLowerCase()
                .normalize('NFD')
                .replace(/[̀-ͯ]/g, '')
                .replace(/[º°ª]/g, '')
                .replace(/[^a-z0-9]+/g, ' ')
                .trim();

            // Tokens "significativos" (palabras > 2 caracteres, sin stopwords comunes)
            const STOP = new Set(['de', 'la', 'el', 'y', 'o', 'a', 'al', 'del', 'los', 'las', 'un', 'una']);
            const tokens = (s: string): string[] =>
              normalize(s)
                .split(' ')
                .filter((t) => t.length > 2 && !STOP.has(t));

            // Mapeo key del booking -> label legible (incluye los 7 predefinidos que
            // configura el profesional, ver utils/patientExtraData.ts)
            const bookingLabelByKey: Record<string, string> = {
              dni: 'DNI',
              edad: 'Edad',
              fecha_nacimiento: 'Fecha de Nacimiento',
              domicilio: 'Domicilio',
              obra_social: 'Obra Social',
              nro_afiliado: 'Nro Afiliado',
              motivo_consulta: 'Motivo de Consulta / Medico Derivante',
              sexo: 'Sexo',
              profesion: 'Profesion',
            };

            // Junta todos los fields del template
            const allFields: any[] = (template.sections || []).flatMap(
              (s: any) => s.fields || []
            );

            // Formatea el valor segun el tipo conocido del booking (ej: fecha)
            const formatExtraValueForField = (key: string, value: any): string => {
              if (key === 'fecha_nacimiento' && value) {
                const parts = String(value).split('-');
                if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
              }
              return String(value);
            };

            for (const [extraKey, extraValueRaw] of Object.entries(extraData)) {
              if (!extraValueRaw) continue;
              const extraValue = formatExtraValueForField(extraKey, extraValueRaw);
              const bookingLabel =
                bookingLabelByKey[extraKey] ||
                extraKey.replace(/_/g, ' ');
              const bookingNorm = normalize(bookingLabel);
              const bookingTokens = tokens(bookingLabel);

              // Buscar el mejor match: priorizamos exact > substring > tokens
              type MatchKind = 'exact' | 'substring' | 'tokens';
              const matches: { field: any; kind: MatchKind }[] = [];

              for (const f of allFields) {
                const labelNorm = normalize(f.field_label || '');
                if (!labelNorm) continue;

                if (labelNorm === bookingNorm) {
                  matches.push({ field: f, kind: 'exact' });
                  continue;
                }
                // Substring: bookingNorm contenido en labelNorm
                if (bookingNorm.length >= 3 && labelNorm.includes(bookingNorm)) {
                  matches.push({ field: f, kind: 'substring' });
                  continue;
                }
                // Tokens: todos los tokens del booking aparecen en label
                if (bookingTokens.length > 0) {
                  const labelTokens = tokens(f.field_label || '');
                  const allPresent = bookingTokens.every((t) =>
                    labelTokens.includes(t)
                  );
                  if (allPresent) {
                    matches.push({ field: f, kind: 'tokens' });
                  }
                }
              }

              // Si hay exact match, usamos solo ese. Sino el primer match disponible.
              const exact = matches.find((m) => m.kind === 'exact');
              const chosen = exact ? [exact] : matches;

              for (const m of chosen) {
                const fk = m.field.field_key;
                if (prefillData[fk]) {
                  // Ya hay valor: concatenamos (caso obra_social + nro_afiliado)
                  prefillData[fk] = `${prefillData[fk]} / ${extraValue}`;
                } else {
                  prefillData[fk] = extraValue;
                }
                prefilledKeySet.add(fk);
              }
            }
          }
        } catch (e) {
          console.error('Error pre-llenando datos del turno:', e);
        }
      }

      setFormData(prefillData);
      setAutofilledKeys(prefilledKeySet);
      setErrors({});

      // Auto-scroll al formulario después de cargar
      setTimeout(() => {
        const formSection = document.getElementById('dynamic-form-section');
        if (formSection) {
          formSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    } catch (err: any) {
      setError('Error al cargar plantilla');
    } finally {
      setLoading(false);
      setLoadingTemplateId(null);
    }
  };

  // Handler para selección de paciente
  const handlePatientSelect = (patientResult: PatientSearchResult | null) => {
    if (patientResult) {
      setPatient({
        patient_id: patientResult.patient_id,
        patient_name: patientResult.patient_name,
        patient_email: patientResult.patient_email,
        patient_phone: patientResult.patient_phone,
        patient_dni: patientResult.patient_dni,
        patient_birth_date: patientResult.patient_birth_date,
        is_new_patient: false
      });
      setIsNewPatient(false);
    } else {
      setPatient(null);
      setIsNewPatient(true);
    }
  };
  
  // Handler para cambios en campos del formulario
  const handleFieldChange = (name: string, value: any) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    // Limpiar error del campo
    if (errors[name]) {
      setErrors(prev => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };
  
  // Handler para cambios en datos de nuevo paciente
  const handleNewPatientChange = (field: string, value: string) => {
    setNewPatientData(prev => ({ ...prev, [field]: value }));
  };
  
  // Validar formulario
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    // Validar paciente
    if (!patient && !isNewPatient) {
      newErrors['patient'] = 'Debe seleccionar o crear un paciente';
    }
    
    if (isNewPatient && !newPatientData.patient_name.trim()) {
      newErrors['patient_name'] = 'El nombre del paciente es requerido';
    }
    
    // Validar plantilla
    if (!selectedTemplate) {
      newErrors['template'] = 'Debe seleccionar una plantilla';
    }
    
    // Validar campos del formulario
    if (selectedTemplate?.sections) {
      const validation = clinicalRecordsApi.validateFormData(formData, selectedTemplate);
      Object.assign(newErrors, validation.errors);
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // Guardar formulario
  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    
    if (!validateForm()) {
      // Scroll al primer error o al resumen
      const errorSummary = document.querySelector('[class*="bg-red-50"]');
      if (errorSummary) {
        errorSummary.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else {
        const firstErrorField = document.querySelector('[class*="border-red"]');
        firstErrorField?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }
    
    setSaving(true);
    setError(null);
    
    try {
      const patientData = isNewPatient 
        ? { ...newPatientData, patient_id: undefined as number | undefined }
        : {
            patient_id: patient?.patient_id,
            patient_name: patient?.patient_name || '',
            patient_email: patient?.patient_email,
            patient_phone: patient?.patient_phone,
            patient_dni: patient?.patient_dni,
            patient_birth_date: patient?.patient_birth_date
          };
      
      if (isEditing && recordId) {
        // Actualizar registro existente
        await clinicalRecordsApi.updateRecord(Number(recordId), {
          form_data: formData,
          notes,
          change_reason: 'Actualización de datos'
        });
      } else {
        // Crear nuevo registro
        const data: CreateRecordRequest = {
          template_id: selectedTemplate!.id,
          patient_name: patientData.patient_name,
          patient_email: patientData.patient_email,
          patient_phone: patientData.patient_phone,
          patient_dni: patientData.patient_dni,
          patient_birth_date: patientData.patient_birth_date,
          patient_id: patientData.patient_id,
          record_date: recordDate,
          form_data: formData,
          notes
        };
        
        await clinicalRecordsApi.createRecord(data);
      }
      
      setSuccess(true);
      setTimeout(() => {
        navigate('/professional/clinical-records');
      }, 1500);
      
    } catch (err: any) {
      console.error('Error guardando:', err);
      setError(err.message || 'Error al guardar la historia clínica');
    } finally {
      setSaving(false);
    }
  };

  // =====================================================
  // RENDER
  // =====================================================
  
  if (loading && !templates.length) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-blue-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-500">Cargando...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/professional/clinical-records')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">
                  {isEditing ? 'Editar Historia Clínica' : 'Nueva Historia Clínica'}
                </h1>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Formulario */}
      <form onSubmit={handleSubmit} className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-6">
          
          {/* Error general */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
              <p className="text-red-700">{error}</p>
            </div>
          )}
          
          {/* Éxito */}
          {success && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
              <p className="text-green-700">Historia clínica guardada correctamente. Redirigiendo...</p>
            </div>
          )}
          
          {/* Paso 1: Seleccionar paciente */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <User className="w-5 h-5 text-blue-500" />
              Paciente
            </h2>
            
            {!isEditing && (
              <PatientSearch
                onSelect={handlePatientSelect}
                selectedPatient={patient}
              />
            )}
            
            {/* Formulario de nuevo paciente */}
            {isNewPatient && (
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nombre completo <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={newPatientData.patient_name}
                    onChange={(e) => handleNewPatientChange('patient_name', e.target.value)}
                    className={`w-full px-4 py-3 bg-gray-50 border rounded-xl focus:ring-2 focus:ring-blue-500 ${
                      errors['patient_name'] ? 'border-red-300' : 'border-gray-200'
                    }`}
                    placeholder="Nombre y apellido del paciente"
                  />
                  {errors['patient_name'] && (
                    <p className="mt-1 text-sm text-red-600">{errors['patient_name']}</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">DNI</label>
                  <input
                    type="text"
                    value={newPatientData.patient_dni}
                    onChange={(e) => handleNewPatientChange('patient_dni', e.target.value)}
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500"
                    placeholder="Documento de identidad"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Fecha de nacimiento</label>
                  <input
                    type="date"
                    value={newPatientData.patient_birth_date}
                    onChange={(e) => handleNewPatientChange('patient_birth_date', e.target.value)}
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={newPatientData.patient_email}
                    onChange={(e) => handleNewPatientChange('patient_email', e.target.value)}
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500"
                    placeholder="correo@ejemplo.com"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Teléfono</label>
                  <input
                    type="tel"
                    value={newPatientData.patient_phone}
                    onChange={(e) => handleNewPatientChange('patient_phone', e.target.value)}
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500"
                    placeholder="+54 9 ..."
                  />
                </div>
              </div>
            )}
            
            {isEditing && patient && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
                    <User className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{patient.patient_name}</p>
                    <p className="text-sm text-gray-500">
                      {patient.patient_dni && `DNI: ${patient.patient_dni}`}
                      {patient.patient_email && ` • ${patient.patient_email}`}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          {/* Paso 2: Seleccionar plantilla */}
          {!isEditing && (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-blue-500" />
                  Tipo de Historia
                </h2>
                <button
                  type="button"
                  onClick={() => navigate('/professional/clinical-records/template/new')}
                  className="flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 hover:bg-blue-50 px-3 py-1.5 rounded-lg transition-colors"
                >
                  <Settings2 className="w-4 h-4" />
                  Crear Plantilla
                </button>
              </div>
              
              {errors['template'] && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                  {errors['template']}
                </div>
              )}

              {/* Plantillas del sistema */}
              {templates.filter(t => !t.professional_id).length > 0 && (
                <div className="mb-5">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Plantillas del Sistema</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                    {templates.filter(t => !t.professional_id).map(template => {
                      const isLoadingThis = loadingTemplateId === template.id;
                      const isAnyLoading = loadingTemplateId !== null;
                      return (
                      <button
                        key={template.id}
                        type="button"
                        onClick={() => handleTemplateSelect(template.id)}
                        disabled={isAnyLoading}
                        className={`relative p-4 rounded-xl border-2 transition-all text-center ${
                          selectedTemplate?.id === template.id
                            ? 'border-blue-500 bg-blue-50 shadow-md'
                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                        } ${isAnyLoading && !isLoadingThis ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        {isLoadingThis && (
                          <div className="absolute inset-0 bg-white/80 rounded-xl flex items-center justify-center z-10">
                            <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
                          </div>
                        )}
                        <div
                          className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-2"
                          style={{ backgroundColor: `${template.color}20`, color: template.color }}
                        >
                          <DynamicIcon name={template.icon} className="w-6 h-6" />
                        </div>
                        <p className="font-medium text-gray-900 text-sm">{template.name}</p>
                        {template.specialty && (
                          <p className="text-xs text-gray-500 mt-1">{template.specialty}</p>
                        )}
                      </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Mis Plantillas (del profesional) */}
              {templates.filter(t => !!t.professional_id).length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Mis Plantillas</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                    {templates.filter(t => !!t.professional_id).map(template => {
                      const isLoadingThis = loadingTemplateId === template.id;
                      const isAnyLoading = loadingTemplateId !== null;
                      return (
                      <div key={template.id} className="relative group">
                        <button
                          type="button"
                          onClick={() => handleTemplateSelect(template.id)}
                          disabled={isAnyLoading}
                          className={`relative w-full p-4 rounded-xl border-2 transition-all text-center ${
                            selectedTemplate?.id === template.id
                              ? 'border-blue-500 bg-blue-50 shadow-md'
                              : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                          } ${isAnyLoading && !isLoadingThis ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          {isLoadingThis && (
                            <div className="absolute inset-0 bg-white/80 rounded-xl flex items-center justify-center z-10">
                              <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
                            </div>
                          )}
                          <div
                            className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-2"
                            style={{ backgroundColor: `${template.color}20`, color: template.color }}
                          >
                            <DynamicIcon name={template.icon} className="w-6 h-6" />
                          </div>
                          <p className="font-medium text-gray-900 text-sm">{template.name}</p>
                          {template.specialty && (
                            <p className="text-xs text-gray-500 mt-1">{template.specialty}</p>
                          )}
                        </button>
                        
                        {/* Botones editar/eliminar - aparecen en hover */}
                        <div className="absolute top-1.5 right-1.5 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/professional/clinical-records/template/${template.id}/edit`);
                            }}
                            className="p-1.5 bg-white rounded-lg shadow-sm border border-gray-200 text-gray-400 hover:text-blue-600 hover:border-blue-300 transition-colors"
                            title="Editar plantilla"
                          >
                            <Edit3 className="w-3.5 h-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (window.confirm(`¿Eliminar la plantilla "${template.name}"?`)) {
                                clinicalRecordsApi.deleteTemplate(template.id)
                                  .then(() => {
                                    setTemplates(prev => prev.filter(t => t.id !== template.id));
                                    if (selectedTemplate?.id === template.id) {
                                      setSelectedTemplate(null);
                                    }
                                  })
                                  .catch(err => console.error('Error eliminando plantilla:', err));
                              }
                            }}
                            className="p-1.5 bg-white rounded-lg shadow-sm border border-gray-200 text-gray-400 hover:text-red-600 hover:border-red-300 transition-colors"
                            title="Eliminar plantilla"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Paso 3: Fecha de la consulta */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-blue-500" />
              Fecha de la Consulta
            </h2>
            
            <input
              type="date"
              value={recordDate}
              onChange={(e) => setRecordDate(e.target.value)}
              className="w-full sm:w-auto px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          {/* Paso 4: Formulario dinámico */}
          {selectedTemplate?.sections && selectedTemplate.sections.length > 0 && (
            <div id="dynamic-form-section" className="space-y-4">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <span
                  className="w-8 h-8 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: `${selectedTemplate.color}20`, color: selectedTemplate.color }}
                >
                  <DynamicIcon name={selectedTemplate.icon} className="w-5 h-5" />
                </span>
                {selectedTemplate.name}
              </h2>
              
              {selectedTemplate.sections
                .sort((a: TemplateSection, b: TemplateSection) => a.display_order - b.display_order)
                .map((section: TemplateSection, index: number) => (
                  <CollapsibleSection
                    key={section.id}
                    section={section}
                    formData={formData}
                    errors={errors}
                    onChange={handleFieldChange}
                    defaultExpanded={index < 2}
                    autofilledKeys={autofilledKeys}
                  />
                ))}
            </div>
          )}
          
          {/* Notas adicionales */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Notas Adicionales
            </h2>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={4}
              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500"
              placeholder="Observaciones, indicaciones especiales, próximos pasos..."
            />
          </div>
          
          {/* Botones de acción */}
          {Object.keys(errors).length > 0 && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
              <p className="text-sm font-medium text-red-800 mb-2">
                Corregí los siguientes campos para poder guardar:
              </p>
              <ul className="space-y-1">
                {Object.entries(errors).map(([key, msg]) => (
                  <li key={key} className="text-sm text-red-600 flex items-start gap-1.5">
                    <span className="mt-0.5">•</span> {msg}
                  </li>
                ))}
              </ul>
            </div>
          )}
          
          <div className="flex flex-col sm:flex-row gap-4 pt-4">
            <button
              type="button"
              onClick={() => navigate('/professional/clinical-records')}
              className="flex-1 sm:flex-none px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors"
            >
              Cancelar
            </button>
            
            <button
              type="button"
              onClick={handleSubmit}
              disabled={saving || success}
              className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-xl font-medium hover:shadow-lg hover:shadow-blue-500/25 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Guardando...
                </>
              ) : success ? (
                <>
                  <CheckCircle className="w-5 h-5" />
                  Guardado
                </>
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  {isEditing ? 'Actualizar Historia' : 'Guardar Historia'}
                </>
              )}
            </button>
          </div>
        </div>
      </form>

      {/* Mini-modal de carga al seleccionar una plantilla: visible mientras se
          descarga la estructura del formulario. Aparece en el centro de la
          pantalla con backdrop sutil para que el usuario tenga feedback de
          que el sistema esta trabajando. */}
      {loadingTemplateId !== null && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 backdrop-blur-sm"
          role="status"
          aria-live="polite"
        >
          <div className="bg-white rounded-2xl shadow-2xl px-6 py-5 flex items-center gap-4 max-w-xs">
            <Loader2 className="w-7 h-7 text-blue-500 animate-spin flex-shrink-0" />
            <div>
              <p className="font-semibold text-gray-900">Cargando plantilla...</p>
              <p className="text-xs text-gray-500 mt-0.5">Preparando los campos del formulario</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClinicalRecordForm;